import { useMutation, useQuery } from '@apollo/client'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import Spin from 'pared/components/basicUi/spin'
import Table from 'pared/components/basicUi/table'
import { IColumnsType } from 'pared/components/basicUi/table'
import COLORS from 'pared/constants/colors'

import {
  IEditPrepItemMenuItemMappingVariables,
  IMenuItemMappingInput,
  IMenuItemsType,
  IPrepItemWithAmountPerSoldItem,
  IUnitsType,
  editPrepItemMenuItemMapping,
  getMenuItems,
  getPrepItemsWithAmountPerSoldItem,
  getUnits,
} from '../gql'

interface IPrepItemsWithAmountPerSoldItemType {
  getPrepItemsWithAmountPerSoldItem: {
    nodes: IPrepItemWithAmountPerSoldItem[]
  }
}

const StyledModal = styled(Modal)`
  .MuiModal-backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
`

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 1100px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`

const Title = styled.h3`
  margin: 0 0 20px 0;
  font-family: Lexend-SemiBold;
  font-size: 18px;
  color: ${COLORS.Chalkboard};
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`

interface MenuItemToPrepItemModalProps {
  visible: boolean
  onClose: () => void
  prepItemName: string
  prepItemId: string
}

interface IPrepItemWithAmountTableData {
  key: string
  menuItemId: number
  menuItem: string
  unit: string
  unitId: number
  amountNeeded: number
  isEditing?: boolean
  isNew?: boolean
}

const MenuItemToPrepItemModal = ({
  visible,
  onClose,
  prepItemName,
  prepItemId,
}: MenuItemToPrepItemModalProps) => {
  const [editMode, setEditMode] = useState(false)
  const [editedData, setEditedData] = useState<IPrepItemWithAmountTableData[]>(
    [],
  )

  const { data, loading, refetch } =
    useQuery<IPrepItemsWithAmountPerSoldItemType>(
      getPrepItemsWithAmountPerSoldItem,
    )

  const { data: menuItemsData, loading: menuItemsLoading } =
    useQuery<IMenuItemsType>(getMenuItems, {
      // Only load menu items when in edit mode to avoid unnecessary queries
      skip: !editMode,
    })

  const { data: unitsData, loading: unitsLoading } = useQuery<IUnitsType>(
    getUnits,
    {
      // Only load units when in edit mode to avoid unnecessary queries
      skip: !editMode,
    },
  )

  const [updateMapping, { loading: saving }] = useMutation<
    any,
    IEditPrepItemMenuItemMappingVariables
  >(editPrepItemMenuItemMapping)

  const originalData = useMemo(() => {
    if (!data?.getPrepItemsWithAmountPerSoldItem?.nodes) return []

    return data.getPrepItemsWithAmountPerSoldItem.nodes
      .filter(
        (item: IPrepItemWithAmountPerSoldItem) =>
          item.prepItemIdCode === prepItemId,
      )
      .map((item: IPrepItemWithAmountPerSoldItem) => ({
        key: `${item.menuItemId}-${item.prepItemIdCode}`,
        menuItemId: item.menuItemId,
        menuItem: item.menuItemName,
        unit: item.unit,
        unitId: item.unitId,
        amountNeeded: item.amountNeededPerSoldItem,
      }))
  }, [data, prepItemId])

  const dataSource = editMode ? editedData : originalData

  const menuItemOptions = useMemo(() => {
    return menuItemsData?.listItems?.nodes || []
  }, [menuItemsData])

  const unitOptions = useMemo(() => {
    return unitsData?.getPrepItemUnits?.nodes || []
  }, [unitsData])

  const handleAddRow = useCallback(() => {
    const newRow: IPrepItemWithAmountTableData = {
      key: `new-${Date.now()}`,
      menuItemId: 0,
      menuItem: '',
      unit: '',
      unitId: 0,
      amountNeeded: 0,
      isNew: true,
    }
    setEditedData((prev) => [...prev, newRow])
  }, [])

  const handleDeleteRow = useCallback((key: string) => {
    setEditedData((prev) => prev.filter((row) => row.key !== key))
  }, [])

  const handleUpdateRow = useCallback(
    (key: string, field: string, value: any) => {
      setEditedData((prev) =>
        prev.map((row) => {
          if (row.key === key) {
            if (field === 'menuItem') {
              const selectedMenuItem = menuItemOptions.find(
                (item) => item.itemDisplayName === value,
              )
              return {
                ...row,
                menuItemId: selectedMenuItem?.itemId || 0,
                menuItem: value,
              }
            }
            if (field === 'unit') {
              const selectedUnit = unitOptions.find(
                (unit) => unit.unitName === value,
              )
              return {
                ...row,
                unitId: selectedUnit?.unitId || 0,
                unit: value,
              }
            }
            return { ...row, [field]: value }
          }
          return row
        }),
      )
    },
    [menuItemOptions, unitOptions],
  )

  const handleSave = async () => {
    try {
      // Validate all rows for completeness
      const incompleteRows: string[] = []
      const validRows: IPrepItemWithAmountTableData[] = []

      editedData.forEach((row, index) => {
        const rowNumber = index + 1
        const missingFields: string[] = []

        if (!row.menuItemId || row.menuItemId === 0) {
          missingFields.push('Menu Item')
        }
        if (!row.unitId || row.unitId === 0) {
          missingFields.push('Unit')
        }
        if (!row.amountNeeded || row.amountNeeded === 0) {
          missingFields.push('Amount')
        }

        if (missingFields.length > 0) {
          incompleteRows.push(
            `Row ${rowNumber}: Missing ${missingFields.join(', ')}`,
          )
        } else {
          validRows.push(row)
        }
      })

      // If there are incomplete rows, show validation message
      if (incompleteRows.length > 0) {
        const message = `Please complete the following rows before saving:\n\n${incompleteRows.join(
          '\n',
        )}`
        alert(message)
        return
      }

      // If no valid rows, show message
      if (validRows.length === 0) {
        alert(
          'Please add at least one complete menu item mapping before saving.',
        )
        return
      }

      // Create mapping input from valid rows only
      const mappingsInput: IMenuItemMappingInput[] = validRows.map((row) => ({
        menu_item_id: row.menuItemId,
        unit_id: row.unitId,
        amount_needed_per_sold_item: row.amountNeeded,
      }))

      await updateMapping({
        variables: {
          iPrepItemId: prepItemId,
          iMappingDetails: mappingsInput,
        } as IEditPrepItemMenuItemMappingVariables,
      })

      // Refetch the data to ensure we get the latest state
      await refetch()
      setEditMode(false)
      setEditedData([])

      // Success message
      alert(`Successfully saved ${validRows.length} menu item mapping(s).`)
    } catch (error) {
      alert('Failed to save changes. Please try again.')
    }
  }

  const columns: IColumnsType<IPrepItemWithAmountTableData>[] = useMemo(() => {
    const baseColumns = [
      {
        key: 'menuItem',
        title: 'Menu Item',
        minWidth: '500px',
        align: 'left' as const,
        render: (_: number, record?: IPrepItemWithAmountTableData) => {
          if (!record) return ''
          if (editMode) {
            return (
              <Autocomplete
                size="small"
                value={record.menuItem}
                onChange={(_, newValue) => {
                  handleUpdateRow(record.key, 'menuItem', newValue || '')
                }}
                options={menuItemOptions.map((item) => item.itemDisplayName)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search menu items..."
                  />
                )}
                loading={menuItemsLoading}
                // Enable efficient client-side filtering with deduplication
                filterOptions={(options, { inputValue }) => {
                  // Remove duplicates first
                  const uniqueOptions = Array.from(new Set(options))

                  if (!inputValue) return uniqueOptions.slice(0, 100) // Show first 100 when no search
                  return uniqueOptions
                    .filter((option) =>
                      option.toLowerCase().includes(inputValue.toLowerCase()),
                    )
                    .slice(0, 50) // Limit results to 50 for performance
                }}
                ListboxProps={{
                  style: { maxHeight: '200px' }, // Limit dropdown height
                }}
                noOptionsText="No menu items found"
              />
            )
          }
          return record.menuItem
        },
      },
      {
        key: 'unit',
        title: 'Unit',
        minWidth: '100px',
        align: 'center' as const,
        render: (_: number, record?: IPrepItemWithAmountTableData) => {
          if (!record) return ''
          if (editMode) {
            return (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={record.unit}
                  label="Unit"
                  onChange={(e) =>
                    handleUpdateRow(record.key, 'unit', e.target.value)
                  }
                  disabled={unitsLoading}
                >
                  {unitOptions.map((unit) => (
                    <MenuItem key={unit.unitId} value={unit.unitName}>
                      {unit.unitName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          }
          return record.unit
        },
      },
      {
        key: 'amountNeeded',
        title: 'Amount Needed Per Sold Item',
        minWidth: '250px',
        align: 'right' as const,
        render: (value: number, record?: IPrepItemWithAmountTableData) => {
          if (!record) return ''
          if (editMode) {
            return (
              <TextField
                size="small"
                type="number"
                value={record.amountNeeded === 0 ? '' : record.amountNeeded}
                onChange={(e) =>
                  handleUpdateRow(
                    record.key,
                    'amountNeeded',
                    parseFloat(e.target.value) || 0,
                  )
                }
                variant="outlined"
                inputProps={{ step: '0.01', min: '0' }}
                placeholder="0"
              />
            )
          }
          return value.toFixed(2)
        },
      },
    ]

    if (editMode) {
      baseColumns.push({
        key: 'actions',
        title: 'Actions',
        minWidth: '100px',
        align: 'center' as const,
        render: (_: number, record?: IPrepItemWithAmountTableData) => {
          if (!record) return ''
          return (
            <IconButton
              size="small"
              onClick={() => handleDeleteRow(record.key)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          )
        },
      })
    }

    return baseColumns
  }, [
    editMode,
    menuItemOptions,
    menuItemsLoading,
    unitOptions,
    unitsLoading,
    handleUpdateRow,
    handleDeleteRow,
  ])

  return (
    <StyledModal open={visible} onClose={onClose}>
      <ModalContent>
        <Title>MenuItem to PrepItem Amount Details - {prepItemName}</Title>
        {editMode && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            style={{ marginBottom: '16px' }}
          >
            Add Menu Item
          </Button>
        )}
        <Spin spinning={loading || saving}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 10 }}
          />
        </Spin>
        <ButtonContainer>
          {!editMode ? (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  setEditedData([...originalData])
                  setEditMode(true)
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                style={{ marginLeft: '10px' }}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditMode(false)
                  setEditedData([])
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                style={{ marginLeft: '10px' }}
              >
                Save Changes
              </Button>
            </>
          )}
        </ButtonContainer>
      </ModalContent>
    </StyledModal>
  )
}

export default MenuItemToPrepItemModal
